import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

class NaProductNavigation extends Component {
    /** @type {Element[]} */
    #sections = [];
    /** @type {Element[]} */
    #navLinks = [];
    /** @type {Element} */
    #navBar;
    /** @type {Element | null} */
    #navList = null;
    /** @type {(() => void) | null} */
    #debouncedSetActiveLink = null;

    connectedCallback() {
        super.connectedCallback();

        // Find tracked sections and navigation elements.
        // Audit note: intentional cross-component lookups in this file (do not flag) — the tracked
        // page sections, scroll targets, and header all live outside this component's subtree,
        // so this.refs/this.querySelector cannot reach them.
        this.#sections = Array.from(document.querySelectorAll('.section-to-track'));
        this.#navLinks = Array.from(this.querySelectorAll('.na-product-nav__list-item'));
        this.#navBar = this.querySelector('.sp-product-nav') || this;
        this.#navList = this.querySelector('.sp-product-nav__list') || this.querySelector('.na-product-nav__list');

        if (this.#navLinks.length > 0) {
            this.#debouncedSetActiveLink = debounce(this.setActiveLink.bind(this), 100);
            window.addEventListener('scroll', this.#debouncedSetActiveLink);
            // Initial run to set active state
            this.setActiveLink();
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.#debouncedSetActiveLink) {
            window.removeEventListener('scroll', this.#debouncedSetActiveLink);
        }
    }

    /**
     * Smooth scrolls to the target section when a navigation item is clicked.
     */
    scrollToSection(event, target) {
        const trigger = target || (event.target && event.target.closest && event.target.closest('.scrollTo'));
        if (!trigger) return;
        event.preventDefault();

        const anchor = '.' + trigger.dataset.anchor;
        const targetElement = document.querySelector(anchor);
        if (!targetElement) return;

        let headerHeight = 0;
        const header = document.querySelector('.header-section');

        if (this.classList.contains('sticky')) {
            headerHeight = this.offsetHeight;
        } else if (header) {
            headerHeight = header.offsetHeight;
        }

        const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - headerHeight;

        window.scrollTo({
            top: targetPosition,
            behavior: 'smooth',
        });
    }

    /**
     * Determines which section is in view and updates the active nav link.
     */
    setActiveLink() {
        let activeSectionFound = false;

        this.#sections.forEach((section) => {
            const rect = section.getBoundingClientRect();

            if (rect.top <= 150 && rect.bottom >= 150) {
                this.#navLinks.forEach((link) => {
                    if (section.classList.contains(link.dataset.anchor)) {
                        link.classList.add('active');
                        activeSectionFound = true;
                        this.scrollToActiveNavItem(link);
                    } else {
                        link.classList.remove('active');
                    }
                });
            }
        });

        let navbarVisible = true;
        if (this.#navBar) {
            navbarVisible = false;
            const rectNav = this.#navBar.getBoundingClientRect();
            if (rectNav.top <= 150 && rectNav.bottom >= 150) {
                navbarVisible = true;
            }
        }

        if (!activeSectionFound && navbarVisible) {
            this.#navLinks.forEach((link) => link.classList.remove('active'));
        }
    }

    /**
     * Horizontally scrolls the navigation list to show the active item.
     */
    scrollToActiveNavItem(activeItem) {
        if (!this.#navList || !activeItem) return;

        this.#navList.scrollTo({
            left: activeItem.offsetLeft - this.#navList.offsetWidth / 2 + activeItem.offsetWidth / 2,
            behavior: 'smooth',
        });
    }
}

if (!customElements.get('na-product-navigation-component')) {
    customElements.define('na-product-navigation-component', NaProductNavigation);
}
